/**
 * Canvas utility functions for floor plan rendering
 */

import { worldToViewScreen } from './geometryUtils';

/**
 * Updates canvas size to match its container
 * @param {HTMLElement} containerRef - Container element reference
 * @param {HTMLCanvasElement} canvasRef - Canvas element reference
 * @returns {Object|null} New canvas dimensions or null if refs not ready
 */
export const updateCanvasSize = (containerRef, canvasRef) => {
  if (!containerRef || !canvasRef) return null;

  const container = containerRef;
  const canvas = canvasRef;
  const rect = container.getBoundingClientRect();

  // Get the computed style to account for padding/borders
  const computedStyle = window.getComputedStyle(container);
  const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
  const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
  const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
  const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

  const width = rect.width - paddingLeft - paddingRight;
  const height = rect.height - paddingTop - paddingBottom;

  // Scale canvas backing store by devicePixelRatio for crisp rendering on HiDPI displays
  const dpr = window.devicePixelRatio || 1;
  const physicalWidth = Math.round(width * dpr);
  const physicalHeight = Math.round(height * dpr);

  // Only update if size actually changed to avoid unnecessary re-renders
  if (canvas.width !== physicalWidth || canvas.height !== physicalHeight) {
    canvas.width = physicalWidth;
    canvas.height = physicalHeight;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    return { width, height };
  }

  return null;
};

/**
 * Sets up a ResizeObserver for the container
 * @param {HTMLElement} containerRef - Container element to observe
 * @param {Function} onResize - Callback function when container resizes
 * @returns {Function} Cleanup function to disconnect observer
 */
export const setupResizeObserver = (containerRef, onResize) => {
  if (!containerRef || !onResize) return () => {};

  const resizeObserver = new ResizeObserver(() => {
    onResize();
  });

  resizeObserver.observe(containerRef);

  return () => {
    resizeObserver.disconnect();
  };
};

/**
 * Clears and prepares canvas for drawing
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
export const clearCanvas = (ctx, width, height) => {
  const dpr = window.devicePixelRatio || 1;

  // Reset to identity transform to clear entire physical canvas
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  // Clear everything first (use physical canvas dimensions)
  const canvas = ctx.canvas;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Then fill with white background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Reset all drawing state
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.lineWidth = 1;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';
  ctx.miterLimit = 10;
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;

  // Enable anti-aliasing for smoother lines
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Apply DPR scaling so all subsequent drawing uses CSS coordinates
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
};

/**
 * Sets up canvas transformation for centered, scaled, and rotated drawing
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {Object} pan - Pan offset {x, y}
 * @param {number} scale - Zoom scale
 * @param {number} rotation - Rotation in degrees
 */
export const setupCanvasTransform = (
  ctx,
  width,
  height,
  pan,
  scale,
  rotation
) => {
  // Center the floor plan
  const centerX = width / 2 + pan.x;
  const centerY = height / 2 + pan.y;

  // Set up the transformation
  ctx.save();

  // Move to the center point
  ctx.translate(centerX, centerY);

  // Apply rotation if needed
  ctx.rotate((rotation * Math.PI) / 180);

  // Apply scale
  ctx.scale(scale, scale);
};

/**
 * Put `ctx` into SCREEN space — CSS pixels, no pan / zoom / rotation — and save
 * what it was, so `ctx.restore()` brings the plan transform back.
 *
 * Screen space is the DEVICE-PIXEL-RATIO transform, NOT the identity matrix.
 * `setupCanvas` scales the context by the DPR, and every screen-space
 * coordinate in this codebase is a CSS pixel; resetting to identity makes the
 * drawing land in device pixels instead, so on a 2× display it comes out at
 * half size and pulled toward the canvas's top-left corner. Both the wiring
 * overlay and the debug axes shipped with that bug — hence one helper.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @returns {number} The device pixel ratio, for anything that needs to re-enter
 *   screen space later without a second `save()`.
 */
export const enterScreenSpace = (ctx) => {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return dpr;
};

/**
 * Switch a context that is mid-plan (inside `setupCanvasTransform`) into SCREEN
 * space for an overlay, and hand back the world → screen mapping that lands on
 * the same pixels the plan just drew on. Pair with `ctx.restore()`.
 *
 * Two things an overlay gets wrong if it rolls its own, and the electrical
 * wiring layer got BOTH:
 *
 *  - **Reset to the DPR transform, not to identity.** `setupCanvas` leaves the
 *    context scaled by `devicePixelRatio`; resetting to identity makes the
 *    overlay draw in DEVICE pixels while its coordinates are still CSS pixels,
 *    so on a 2× display every point lands halfway back toward the top-left
 *    corner — an offset that grows with distance from it.
 *  - **Honour `rotation`.** `setupCanvasTransform` rotates; an overlay that
 *    only centres, pans and scales is off by the plan's rotation.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context, transform applied
 * @param {import('./geometryUtils').PlanView} view - The plan's view, CSS px
 * @returns {(worldX: number, worldZ: number) => {x: number, y: number}}
 */
export const beginScreenSpaceOverlay = (ctx, view) => {
  enterScreenSpace(ctx);
  return (worldX, worldZ) => worldToViewScreen(worldX, worldZ, view);
};

/**
 * Restores canvas transform state
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 */
export const restoreCanvasTransform = (ctx) => {
  ctx.restore();
};
